import { ApiConfigData, EnvironmentData, OrganizationData } from '~/api';
import { getEnvironmentUrl } from '~/pages/ApiCredentials/utils.tsx';
import { ResourceContextData } from '~/api/types/resourceContextData.ts';
import { isFeatureEnabled } from '~/utils/features/featureFlag.tsx';
import { FeatureFlagKeys } from '~/api/types/featureFlagData.ts';
import {
  AppWindowIcon,
  KeyIcon,
  ShieldCheckIcon,
  TimerIcon,
  UsersIcon
} from 'lucide-solid';
import { getTranslationKeyFunction } from '~/utils/commonUtils.ts';

const getTranslationKey = (key: string) => `pages.quick_start.${key}`;

export enum QuickStartModuleTypes {
  SSO = 'sso',
  SCIM = 'scim',
  AGENT_AUTH = 'agent-auth',
  MCP_AUTH = 'mcp-auth',
  FSA = 'fsa',
  PASSWORDLESS = 'passwordless'
}

const getInitiateSsoOauthAuthorizeUrl = ({
  environment,
  client,
  organization
}: {
  environment: EnvironmentData;
  client: ApiConfigData;
  organization: OrganizationData;
}) => {
  const redirectUri = getRedirectUriForSSO(client);
  return `${getEnvironmentUrl(environment)}/oauth/authorize?response_type=code&client_id=${client.keyId}&redirect_uri=${redirectUri}&scope=openid profile email&organization_id=${organization.id}`;
};

const getModuleKey = (
  moduleType: QuickStartModuleTypes
): keyof ResourceContextData | null => {
  switch (moduleType) {
    case QuickStartModuleTypes.SSO:
      return 'ssoOnboardingDone';
    case QuickStartModuleTypes.SCIM:
      return 'scimOnboardingDone';
    case QuickStartModuleTypes.FSA:
      return 'fsaQuickstartVisited';
    default:
      return null;
  }
};

const isQuickStartForModuleExplored = (
  resourceContextData: ResourceContextData,
  moduleType: QuickStartModuleTypes
) => {
  return getModuleKey(moduleType)
    ? resourceContextData[getModuleKey(moduleType)!]
    : false;
};

const isQuickStartNotExploredYet = (
  resourceContextData: ResourceContextData
) => {
  return !Object.values(QuickStartModuleTypes).some((moduleType) => {
    return isQuickStartForModuleExplored(resourceContextData, moduleType);
  });
};

const getRedirectUriForSSO = (client: ApiConfigData): string => {
  return isFeatureEnabled(FeatureFlagKeys.FSA_REDIRECT_URI)
    ? client.postLoginUris?.[0]
    : client.defaultRedirectUri;
};

const getFsaFeatureTagTranslationKey = getTranslationKeyFunction(
  'pages.quick_start.fsa_features'
);

const FSA_FEATURES_TAG_CONFIG = [
  {
    icon: AppWindowIcon,
    titleTranslationKey: 'hosted_login_signup_page'
  },
  {
    icon: KeyIcon,
    titleTranslationKey: 'auth_methods'
  },
  {
    icon: TimerIcon,
    titleTranslationKey: 'session_management'
  },
  {
    icon: UsersIcon,
    titleTranslationKey: 'user_management'
  },
  {
    icon: ShieldCheckIcon,
    titleTranslationKey: 'roles_permissions'
  }
];

const TEST_ID_PREFIX: Record<QuickStartModuleTypes, string> = {
  [QuickStartModuleTypes.SSO]: 'sso',
  [QuickStartModuleTypes.SCIM]: 'scim',
  [QuickStartModuleTypes.MCP_AUTH]: 'mcp-auth',
  [QuickStartModuleTypes.AGENT_AUTH]: 'agent-auth',
  [QuickStartModuleTypes.PASSWORDLESS]: 'passwordless',
  [QuickStartModuleTypes.FSA]: 'fsa'
};

const getQuickstartTestId = (
  moduleType: QuickStartModuleTypes,
  suffix: string
) => `quickstart-${TEST_ID_PREFIX[moduleType] ?? 'generic'}-${suffix}`;

export {
  FSA_FEATURES_TAG_CONFIG,
  TEST_ID_PREFIX,
  getTranslationKey,
  getFsaFeatureTagTranslationKey,
  getInitiateSsoOauthAuthorizeUrl,
  getModuleKey,
  getRedirectUriForSSO,
  isQuickStartForModuleExplored,
  isQuickStartNotExploredYet,
  getQuickstartTestId
};
